import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';
import { Role, RolePermissions } from './role';

/**
 * Fonction utilitaire pour transformer les timestamps Firestore en Date
 */
const transformFirestoreTimestamp = (val: any): Date => {
  if (val instanceof Timestamp) {
    return val.toDate();
  }
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'string') {
    return new Date(val);
  }
  if (val && typeof val === 'object' && val.seconds !== undefined) {
    // Timestamp Firestore sous forme d'objet
    return new Date(val.seconds * 1000);
  }
  throw new Error(`Cannot transform timestamp: ${val}`);
};

/**
 * Schéma Zod pour les timestamps Firestore
 */
const firestoreTimestampSchema = z.any().transform(transformFirestoreTimestamp);

/**
 * Schéma de validation pour l'affiliation d'un utilisateur à une organisation
 * Note: Les permissions sont maintenant stockées dans la collection roles
 */
export const UserAffiliationSchema = z.object({
  id: z.string(),
  customerId: z.string().min(1, 'L\'ID customer est requis'), // ID du customer (identifiant business unique)
  email: z.string().email('Email invalide').optional(), // ✅ Email de l'utilisateur affilié
  organizationId: z.string().min(1, 'L\'ID organisation est requis'),

  // Référence au rôle (nom du rôle dans la collection roles)
  roleName: z.string().min(1, 'Le nom du rôle est requis').default('member'),

  // Statut de l'affiliation
  status: z.enum(['active', 'inactive', 'pending', 'suspended']).default('active'),

  // Préférences de notifications
  emailNotifications: z.object({
    savMessages: z.boolean().default(true), // Recevoir les notifications SAV par email
  }).optional(),

  // Métadonnées
  joinedAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,

  // Informations sur l'invitation
  invitedBy: z.string().optional(),
  invitedAt: firestoreTimestampSchema.optional(),

  // Dernière activité
  lastActivityAt: firestoreTimestampSchema.optional(),
});

/**
 * Interface TypeScript pour l'affiliation d'un utilisateur (base)
 * Note: Les permissions sont résolues via la jointure avec la collection roles
 */
export interface UserAffiliationBase {
  id: string;
  customerId: string; // ID du customer (identifiant business unique)
  email?: string; // ✅ Email de l'utilisateur affilié
  organizationId: string;

  // Référence au rôle (nom du rôle dans la collection roles)
  roleName: string;

  // Statut de l'affiliation
  status: 'active' | 'inactive' | 'pending' | 'suspended';

  // Préférences de notifications
  emailNotifications?: {
    savMessages: boolean; // Recevoir les notifications SAV par email
  };

  // Métadonnées
  joinedAt: Date;
  updatedAt: Date;

  // Informations sur l'invitation
  invitedBy?: string;
  invitedAt?: Date;

  // Dernière activité
  lastActivityAt?: Date;
}

/**
 * Interface TypeScript pour l'affiliation d'un utilisateur avec le rôle résolu
 * Cette interface est utilisée dans l'application après la jointure avec la collection roles
 */
export interface UserAffiliation extends UserAffiliationBase {
  // Rôle résolu (chargé depuis la collection roles)
  roleData?: Role;

  // Permissions résolues depuis le rôle (pour compatibilité avec le code existant)
  permissions?: RolePermissions;

  // Alias pour compatibilité avec l'ancien code (utilise roleName en interne)
  role?: 'owner' | 'admin' | 'member' | 'viewer';
}

/**
 * Interface pour créer une nouvelle affiliation
 */
export interface CreateUserAffiliationRequest {
  firebaseUid: string; // Firebase Auth UID - REQUIS pour la sécurité
  customerId: string; // ID du customer (identifiant business unique) - REQUIS
  email?: string; // ✅ Email de l'utilisateur affilié
  organizationId: string;
  roleName?: string; // Nom du rôle (par défaut: 'member')
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  invitedBy?: string;
}

/**
 * Génère l'ID structuré pour une affiliation
 * Format: {firebaseUid}_{organizationId}
 */
export function generateAffiliationId(firebaseUid: string, organizationId: string): string {
  if (!firebaseUid || !organizationId) {
    throw new Error('firebaseUid et organizationId sont requis pour générer l\'ID d\'affiliation');
  }
  return `${firebaseUid}_${organizationId}`;
}

/**
 * Interface pour mettre à jour une affiliation
 */
export interface UpdateUserAffiliationRequest {
  roleName?: string; // Nom du rôle à mettre à jour
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  lastActivityAt?: Date;
}

/**
 * @deprecated Utilisez RoleRepository.getByName() pour obtenir les permissions d'un rôle
 * Cette constante est conservée pour compatibilité avec l'ancien code pendant la migration
 */
export const ROLE_PERMISSIONS: Record<string, RolePermissions> = {
  owner: {
    canViewOrders: true,
    canManageOrders: true,
    canManageConfiguration: true,
    canViewAnalytics: true,
    canManageUsers: true,
  },
  admin: {
    canViewOrders: true,
    canManageOrders: true,
    canManageConfiguration: true,
    canViewAnalytics: true,
    canManageUsers: true,
  },
  member: {
    canViewOrders: true,
    canManageOrders: true,
    canManageConfiguration: false,
    canViewAnalytics: true,
    canManageUsers: false,
  },
  viewer: {
    canViewOrders: true,
    canManageOrders: false,
    canManageConfiguration: false,
    canViewAnalytics: false,
    canManageUsers: false,
  },
};

/**
 * @deprecated Utilisez RoleRepository.getByName() pour obtenir les permissions d'un rôle
 * Cette fonction est conservée pour compatibilité avec l'ancien code pendant la migration
 */
export const createDefaultPermissions = (roleName: string): RolePermissions => {
  return ROLE_PERMISSIONS[roleName] || ROLE_PERMISSIONS.member;
};

/**
 * Fonction utilitaire pour vérifier si un utilisateur a une permission spécifique
 */
export const hasPermission = (affiliation: UserAffiliation, permission: keyof RolePermissions): boolean => {
  if (!affiliation.permissions) {
    return false;
  }
  return affiliation.permissions[permission];
};

/**
 * Fonction utilitaire pour vérifier si un utilisateur est propriétaire d'une organisation
 */
export const isOwner = (affiliation: UserAffiliation): boolean => {
  return affiliation.roleName === 'owner' || (affiliation.role as any) === 'owner';
};

/**
 * Fonction utilitaire pour vérifier si un utilisateur est administrateur
 */
export const isAdmin = (affiliation: UserAffiliation): boolean => {
  const roleName = affiliation.roleName || (affiliation.role as any);
  return roleName === 'admin' || roleName === 'owner';
};









